import { useEffect, useMemo, useState } from 'react'
import AdminShell from './AdminShell'
import {
  AdminButton,
  AdminCard,
  AdminCardHeader,
  AdminDataTable,
  AdminField,
  AdminModal,
  AdminPagination,
  AdminSelect,
  AdminStatePanel,
  AdminStatusPill,
  AdminTabPanel,
  AdminTabs,
  type AdminStatusTone,
  type AdminTableColumn,
} from './AdminUi'
import '../styles/admin-email-notifications.css'

const RECOVERY_SOURCES: Record<string, string> = {
  transactional_email_events: 'Order emails', order_owner_alert_events: 'Owner order alerts',
  support_email_events: 'Support replies', support_alert_events: 'Owner support alerts',
  admin_email_test_events: 'Owner test emails',
}
const RECOVERY_REASONS: Record<string, string> = {
  'legacy-acceptance-unknown': 'Original acceptance is unknown',
  'email-idempotency-window-expired': 'The safe replay window has elapsed',
  'email-retries-exhausted': 'The retry limit was reached',
  'email-transport-identity-changed': 'Provider configuration changed',
  'email-provider-key-already-journalled': 'This request already belongs to another retained email event',
  'email-idempotency-key-invalid': 'The message request is invalid',
}
type EventType = 'card-paid' | 'bank-awaiting-payment' | 'bank-payment-received' | 'order-delivered'
type EventStatus = 'pending' | 'sending' | 'sent' | 'failed'
type Session = { authenticated: true; user: { email: string; role: string }; csrfToken: string }
type RecipientConfig = { configured: boolean; valid: boolean; recipients: string[] }
type TemplateDefinition = { eventType: EventType; name: string; trigger: string; description: string }
type RecentTest = {
  event_type: EventType
  recipient_email: string
  status: EventStatus
  provider_email_id: string | null
  attempt_count: number
  last_error: string | null
  created_at: string
  sent_at: string | null
}
type Overview = {
  metrics: { sentToday: number; failed: number; pending: number }
  recovery: Array<{ source_table: string; state: string; outcome: string; reason: string | null; count: number }>
  templates: TemplateDefinition[]
  transport: {
    provider: string
    transactional: { configured: boolean; from: string | null; replyTo: string | null; apiKeyConfigured: boolean }
    supportAlerts: { configured: boolean; from: string | null; recipients: RecipientConfig }
    orderAlerts: { configured: boolean; from: string | null; recipients: RecipientConfig }
  }
  allowedTestRecipients: string[]
  recentTests: RecentTest[]
  semantics: { sent: string; failed: string; pending: string }
}
type ActivityItem = {
  id: number
  public_order_id: string
  event_type: EventType
  recipient_email: string
  status: EventStatus
  provider_message_id: string | null
  attempt_count: number
  last_error: string | null
  created_at: string
  updated_at: string
  sent_at: string | null
}
type InternalItem = {
  id: string
  source: 'support' | 'order' | 'volley'
  event_type: 'new_ticket' | 'customer_reply' | 'new_order' | 'volley_attempt'
  reference: string
  customer_name: string | null
  status: EventStatus
  provider_email_id: string | null
  attempt_count: number
  last_error: string | null
  created_at: string
  sent_at: string | null
}
type Paged<T> = { page: number; total: number; totalPages: number; items: T[] }

const NZ_TIME = new Intl.DateTimeFormat('en-NZ', {
  timeZone: 'Pacific/Auckland',
  day: 'numeric',
  month: 'short',
  year: 'numeric',
  hour: 'numeric',
  minute: '2-digit',
})

function environmentLabel() {
  const host = window.location.hostname.toLowerCase()
  if (host.includes('staging')) return 'Staging'
  if (host === 'localhost' || host === '127.0.0.1') return 'Local'
  return undefined
}

function loginUrl() {
  return `/admin/login?next=${encodeURIComponent(`${window.location.pathname}${window.location.search}`)}`
}

function when(value: string | null | undefined) {
  if (!value) return '—'
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? '—' : NZ_TIME.format(date)
}

function statusTone(status: EventStatus): AdminStatusTone {
  if (status === 'sent') return 'success'
  if (status === 'failed') return 'danger'
  if (status === 'sending') return 'info'
  return 'warning'
}

function statusLabel(status: EventStatus) {
  if (status === 'sent') return 'Sent'
  if (status === 'failed') return 'Failed'
  if (status === 'sending') return 'Sending'
  return 'Pending'
}

function internalLabel(item: InternalItem) {
  if (item.source === 'volley') return 'Volley attempt review'
  if (item.source === 'order') return 'New order'
  return item.event_type === 'customer_reply' ? 'Customer replied' : 'New support ticket'
}

export default function AdminEmailNotificationsPage() {
  const [session, setSession] = useState<Session | null>(null)
  const [overview, setOverview] = useState<Overview | null>(null)
  const [activity, setActivity] = useState<Paged<ActivityItem> | null>(null)
  const [internal, setInternal] = useState<Paged<InternalItem> | null>(null)
  const [activeTab, setActiveTab] = useState('transactional')
  const [error, setError] = useState('')
  const [notice, setNotice] = useState('')
  const [logoutBusy, setLogoutBusy] = useState(false)
  const [previewEvent, setPreviewEvent] = useState<EventType | null>(null)
  const [testEvent, setTestEvent] = useState<EventType | null>(null)
  const [testRecipient, setTestRecipient] = useState('')
  const [testBusy, setTestBusy] = useState(false)
  const env = useMemo(environmentLabel, [])

  const templateNames = useMemo(() => {
    const map = new Map<EventType, string>()
    overview?.templates.forEach((template) => map.set(template.eventType, template.name))
    return map
  }, [overview])

  async function api<T>(path: string): Promise<T> {
    const response = await fetch(path, { credentials: 'same-origin', cache: 'no-store' })
    if (response.status === 401) {
      window.location.replace(loginUrl())
      throw new Error('Sign in required.')
    }
    const payload = await response.json().catch(() => null) as (T & { message?: string }) | null
    if (!response.ok || !payload) throw new Error(payload?.message || 'Email & Notifications could not be loaded.')
    return payload
  }

  async function loadPage(activityPage = 1, internalPage = 1) {
    setError('')
    const sessionResponse = await fetch('/api/admin/session', { credentials: 'same-origin', cache: 'no-store' })
    if (sessionResponse.status === 401) {
      window.location.replace(loginUrl())
      return
    }
    if (!sessionResponse.ok) throw new Error('Boss Zone security could not be verified.')
    const nextSession = await sessionResponse.json() as Session
    const [nextOverview, nextActivity, nextInternal] = await Promise.all([
      api<Overview>('/api/admin/email-notifications'),
      api<Paged<ActivityItem>>(`/api/admin/email-notifications/activity?page=${activityPage}`),
      api<Paged<InternalItem>>(`/api/admin/email-notifications/internal-notifications?page=${internalPage}`),
    ])
    setSession(nextSession)
    setOverview(nextOverview)
    setActivity(nextActivity)
    setInternal(nextInternal)
  }

  useEffect(() => {
    void loadPage().catch((caught) => setError(caught instanceof Error ? caught.message : 'Email & Notifications could not be loaded.'))
  }, [])

  async function logout() {
    if (!session || logoutBusy) return
    setLogoutBusy(true)
    try {
      const response = await fetch('/api/admin/logout', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'X-CSRF-Token': session.csrfToken },
      })
      if (!response.ok) throw new Error('Logout failed.')
      window.location.replace('/admin/login')
    } catch {
      setError('Logout could not be completed.')
      setLogoutBusy(false)
    }
  }

  function openTest(template: TemplateDefinition) {
    setNotice('')
    setTestEvent(template.eventType)
    setTestRecipient(overview?.allowedTestRecipients[0] ?? '')
  }

  async function sendTest() {
    if (!session || !testEvent || !testRecipient || testBusy) return
    setTestBusy(true)
    setError('')
    setNotice('')
    try {
      const response = await fetch('/api/admin/email-notifications/test-send', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': session.csrfToken },
        body: JSON.stringify({
          eventType: testEvent,
          recipientEmail: testRecipient,
          idempotencyKey: crypto.randomUUID(),
        }),
      })
      const payload = await response.json().catch(() => null) as { status?: string; message?: string } | null
      if (!response.ok) throw new Error(payload?.message || 'The test email could not be sent.')
      setNotice(payload?.status === 'intervention'
        ? 'This test email needs owner review. Check Email recovery before sending another test.'
        : payload?.status === 'pending' || payload?.status === 'in-progress'
        ? 'This test email is queued or being processed.'
        : payload?.status === 'already-sent' ? 'That test email had already been accepted by Resend.' : 'Test email accepted by Resend.')
      setTestEvent(null)
      await loadPage(activity?.page ?? 1, internal?.page ?? 1)
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'The test email could not be sent.')
    } finally {
      setTestBusy(false)
    }
  }

  const activityColumns: AdminTableColumn<ActivityItem>[] = [
    {
      key: 'when',
      label: 'Time',
      width: '18%',
      render: (row) => <span className="email-notifications__muted">{when(row.sent_at ?? row.updated_at)}</span>,
    },
    {
      key: 'order',
      label: 'Order',
      render: (row) => <strong>{row.public_order_id}</strong>,
    },
    {
      key: 'template',
      label: 'Template',
      render: (row) => (
        <div className="email-notifications__table-primary">
          <strong>{templateNames.get(row.event_type) ?? row.event_type}</strong>
          <span>{row.recipient_email}</span>
        </div>
      ),
    },
    {
      key: 'status',
      label: 'Status',
      render: (row) => (
        <div className="email-notifications__status-cell">
          <AdminStatusPill tone={statusTone(row.status)}>{statusLabel(row.status)}</AdminStatusPill>
          {row.last_error && <span title={row.last_error}>{row.last_error}</span>}
        </div>
      ),
    },
    {
      key: 'attempts',
      label: 'Attempts',
      align: 'right',
      render: (row) => row.attempt_count,
    },
  ]

  const internalColumns: AdminTableColumn<InternalItem>[] = [
    {
      key: 'when',
      label: 'Time',
      width: '20%',
      render: (row) => <span className="email-notifications__muted">{when(row.sent_at ?? row.created_at)}</span>,
    },
    {
      key: 'event',
      label: 'Event',
      render: (row) => (
        <div className="email-notifications__table-primary">
          <strong>{internalLabel(row)}</strong>
          <span>{row.source === 'support' ? 'Support owner alert' : 'New-order owner alert'}</span>
        </div>
      ),
    },
    {
      key: 'reference',
      label: 'Reference',
      render: (row) => (
        <div className="email-notifications__table-primary">
          <strong>{row.reference}</strong>
          {row.customer_name && <span>{row.customer_name}</span>}
        </div>
      ),
    },
    {
      key: 'status',
      label: 'Status',
      render: (row) => (
        <div className="email-notifications__status-cell">
          <AdminStatusPill tone={statusTone(row.status)}>{statusLabel(row.status)}</AdminStatusPill>
          {row.last_error && <span title={row.last_error}>{row.last_error}</span>}
        </div>
      ),
    },
    {
      key: 'attempts',
      label: 'Attempts',
      align: 'right',
      render: (row) => row.attempt_count,
    },
  ]

  if (error && !session) {
    return (
      <main className="admin-shell-gate-error">
        <AdminStatePanel
          state="error"
          title="Email & Notifications unavailable"
          description={error}
          action={<AdminButton type="button" onClick={() => window.location.reload()}>Retry</AdminButton>}
        />
      </main>
    )
  }

  if (!session || !overview || !activity || !internal) {
    return <main className="admin-shell-gate-loading"><AdminStatePanel state="loading" title="Loading Email & Notifications" description="Reading the authoritative email event logs." /></main>
  }

  return (
    <AdminShell
      title="Email & Notifications"
      eyebrow="CONTENT & COMMS"
      description="Monitor real customer transactional email and internal owner alerts. Sent means Resend accepted the send; delivery confirmation is not persisted."
      user={session.user}
      onLogout={() => void logout()}
      logoutBusy={logoutBusy}
      environmentLabel={env}
    >
      {error && <div className="email-notifications__banner email-notifications__banner--error" role="alert">{error}</div>}
      {notice && <div className="email-notifications__banner email-notifications__banner--success" role="status">{notice}</div>}

      <AdminTabs
        idBase="email-notifications-tabs"
        tabs={[
          { id: 'transactional', label: 'Transactional Email' },
          { id: 'internal', label: 'Internal Notifications' },
        ]}
        activeId={activeTab}
        onChange={setActiveTab}
        label="Email and notification sections"
      />

      <AdminTabPanel idBase="email-notifications-tabs" tabId="transactional" active={activeTab === 'transactional'}>
        <div className="email-notifications__metrics" aria-label="Transactional email summary">
          <AdminCard className="email-notifications__metric-card">
            <span>Sent today</span>
            <strong>{overview.metrics.sentToday}</strong>
            <small>Accepted by Resend today · NZ time</small>
          </AdminCard>
          <AdminCard className="email-notifications__metric-card">
            <span>Failed</span>
            <strong>{overview.metrics.failed}</strong>
            <small>Current failed customer email events</small>
          </AdminCard>
          <AdminCard className="email-notifications__metric-card">
            <span>Pending</span>
            <strong>{overview.metrics.pending}</strong>
            <small>Queued customer email events</small>
          </AdminCard>
        </div>

        <AdminCard className="email-notifications__templates">
          <AdminCardHeader
            title="Transactional Email"
            description="Customer transactional templates. Production copy remains code-controlled and read-only here."
            action={(
              <AdminStatusPill tone={overview.transport.transactional.configured ? 'success' : 'danger'}>
                {overview.transport.transactional.configured ? 'Operational' : 'Configuration issue'}
              </AdminStatusPill>
            )}
          />
          <div className="email-notifications__template-list">
            {overview.templates.map((template) => (
              <article className="email-notifications__template-row" key={template.eventType}>
                <div className="email-notifications__template-icon" aria-hidden="true">✉</div>
                <div className="email-notifications__template-copy">
                  <div className="email-notifications__template-heading">
                    <strong>{template.name}</strong>
                    <AdminStatusPill tone="success">Active</AdminStatusPill>
                  </div>
                  <span>{template.trigger}</span>
                  <p>{template.description}</p>
                </div>
                <div className="email-notifications__template-actions">
                  <AdminButton variant="secondary" compact type="button" onClick={() => setPreviewEvent(template.eventType)}>Preview</AdminButton>
                  <AdminButton compact type="button" disabled={!overview.allowedTestRecipients.length} onClick={() => openTest(template)}>Send test</AdminButton>
                </div>
              </article>
            ))}
          </div>
        </AdminCard>

        <div className="email-notifications__content-split">
          <AdminCard padding="none" className="email-notifications__activity-card">
            <div className="email-notifications__card-pad">
              <AdminCardHeader
                title="Recent email activity"
                description="Authoritative customer transactional email events stored by Sugar Gremlin."
              />
            </div>
            {activity.items.length ? (
              <>
                <AdminDataTable columns={activityColumns} rows={activity.items} rowKey={(row) => String(row.id)} caption="Recent transactional email activity" />
                <div className="email-notifications__pagination">
                  <AdminPagination
                    page={activity.page}
                    totalPages={activity.totalPages}
                    onPageChange={(page) => void loadPage(page, internal.page).catch((caught) => setError(caught instanceof Error ? caught.message : 'Activity could not be loaded.'))}
                  />
                </div>
              </>
            ) : (
              <div className="email-notifications__empty">No customer transactional email events have been recorded yet.</div>
            )}
          </AdminCard>

          <aside className="email-notifications__settings-stack" aria-label="Email settings">
            <AdminCard>
              <AdminCardHeader title="Email settings" description="Read-only V1 infrastructure state." />
              <dl className="email-notifications__settings-list">
                <div><dt>Provider</dt><dd>{overview.transport.provider}</dd></div>
                <div><dt>From</dt><dd>{overview.transport.transactional.from ?? 'Not configured'}</dd></div>
                <div><dt>Reply-To</dt><dd>{overview.transport.transactional.replyTo ?? 'Not configured'}</dd></div>
                <div><dt>API key</dt><dd><AdminStatusPill tone={overview.transport.transactional.apiKeyConfigured ? 'success' : 'danger'}>{overview.transport.transactional.apiKeyConfigured ? 'Configured' : 'Missing'}</AdminStatusPill></dd></div>
              </dl>
              <p className="email-notifications__settings-note">Sender, DNS, credentials and transport architecture are Development-controlled in V1.</p>
            </AdminCard>

            <AdminCard>
              <AdminCardHeader title="Email recovery" description="Deferred emails retry automatically while safe. Emails needing review are held to prevent duplicates." />
              <div className="email-notifications__semantics">
                <p><strong>Needs owner review</strong><span>{overview.recovery.filter(item => item.state === 'intervention').reduce((total, item) => total + item.count, 0)}</span></p>
                <p><strong>Waiting to retry</strong><span>{overview.recovery.filter(item => item.state === 'retry').reduce((total, item) => total + item.count, 0)}</span></p>
                <p><strong>Being processed</strong><span>{overview.recovery.filter(item => item.state === 'sending').reduce((total, item) => total + item.count, 0)}</span></p>
                {overview.recovery.filter(item => item.state === 'intervention').map(item => (
                  <p key={`${item.source_table}/${item.outcome}/${item.reason}`}><strong>{RECOVERY_SOURCES[item.source_table] ?? 'Email'}</strong><span>{item.count} held: {RECOVERY_REASONS[item.reason ?? ''] ?? 'Owner review required'}; acceptance {item.outcome}.</span></p>
                ))}
              </div>
            </AdminCard>

            <AdminCard>
              <AdminCardHeader title="Status semantics" />
              <div className="email-notifications__semantics">
                <p><strong>Sent</strong><span>{overview.semantics.sent}</span></p>
                <p><strong>Failed</strong><span>{overview.semantics.failed}</span></p>
                <p><strong>Pending</strong><span>{overview.semantics.pending}</span></p>
              </div>
            </AdminCard>

            <AdminCard>
              <AdminCardHeader title="Recent test sends" description="Owner-safe tests only; no customer order is created." />
              {overview.recentTests.length ? (
                <div className="email-notifications__test-list">
                  {overview.recentTests.map((test, index) => (
                    <div key={`${test.created_at}-${index}`}>
                      <span>{templateNames.get(test.event_type) ?? test.event_type}</span>
                      <small>{test.recipient_email}</small>
                      <AdminStatusPill tone={statusTone(test.status)}>{statusLabel(test.status)}</AdminStatusPill>
                    </div>
                  ))}
                </div>
              ) : <p className="email-notifications__settings-note">No test sends yet.</p>}
            </AdminCard>
          </aside>
        </div>
      </AdminTabPanel>

      <AdminTabPanel idBase="email-notifications-tabs" tabId="internal" active={activeTab === 'internal'}>
        <div className="email-notifications__internal-state">
          <AdminCard>
            <div className="email-notifications__service-row">
              <div><strong>Support owner alerts</strong><span>New Support tickets and customer email replies</span></div>
              <AdminStatusPill tone={overview.transport.supportAlerts.configured ? 'success' : 'danger'}>{overview.transport.supportAlerts.configured ? 'Configured' : 'Needs configuration'}</AdminStatusPill>
            </div>
            <div className="email-notifications__recipient-list">
              {overview.transport.supportAlerts.recipients.recipients.length
                ? overview.transport.supportAlerts.recipients.recipients.map((recipient) => <span key={recipient}>{recipient}</span>)
                : <span>No recipients configured</span>}
            </div>
          </AdminCard>
          <AdminCard>
            <div className="email-notifications__service-row">
              <div><strong>New-order owner alerts</strong><span>Successful card orders and created bank-transfer orders</span></div>
              <AdminStatusPill tone={overview.transport.orderAlerts.configured ? 'success' : 'danger'}>{overview.transport.orderAlerts.configured ? 'Configured' : 'Needs configuration'}</AdminStatusPill>
            </div>
            <div className="email-notifications__recipient-list">
              {overview.transport.orderAlerts.recipients.recipients.length
                ? overview.transport.orderAlerts.recipients.recipients.map((recipient) => <span key={recipient}>{recipient}</span>)
                : <span>No recipients configured</span>}
            </div>
          </AdminCard>
        </div>

        <AdminCard padding="none" className="email-notifications__activity-card">
          <div className="email-notifications__card-pad">
            <AdminCardHeader
              title="Internal notification activity"
              description="Durable owner-email alert events only. No SMS, push or generic notification platform."
            />
          </div>
          {internal.items.length ? (
            <>
              <AdminDataTable columns={internalColumns} rows={internal.items} rowKey={(row) => `${row.source}-${row.id}`} caption="Internal owner notification activity" />
              <div className="email-notifications__pagination">
                <AdminPagination
                  page={internal.page}
                  totalPages={internal.totalPages}
                  onPageChange={(page) => void loadPage(activity.page, page).catch((caught) => setError(caught instanceof Error ? caught.message : 'Internal notifications could not be loaded.'))}
                />
              </div>
            </>
          ) : (
            <div className="email-notifications__empty">No internal owner notification events have been recorded yet.</div>
          )}
        </AdminCard>
      </AdminTabPanel>

      <AdminModal
        open={previewEvent !== null}
        title={previewEvent ? `${templateNames.get(previewEvent) ?? 'Transactional email'} preview` : 'Email preview'}
        description="Rendered by the existing production transactional email renderer using synthetic preview-order data."
        onClose={() => setPreviewEvent(null)}
        footer={<AdminButton variant="secondary" type="button" onClick={() => setPreviewEvent(null)}>Close</AdminButton>}
      >
        {previewEvent && (
          <iframe
            className="email-notifications__preview-frame"
            title={`${templateNames.get(previewEvent) ?? previewEvent} preview`}
            src={`/api/admin/email-notifications/templates/${encodeURIComponent(previewEvent)}/preview`}
          />
        )}
      </AdminModal>

      <AdminModal
        open={testEvent !== null}
        title={testEvent ? `Send ${templateNames.get(testEvent) ?? 'email'} test` : 'Send test email'}
        description="Tests use the existing template renderer and can only be sent to configured owner notification inboxes."
        onClose={() => { if (!testBusy) setTestEvent(null) }}
        footer={(
          <>
            <AdminButton variant="secondary" type="button" disabled={testBusy} onClick={() => setTestEvent(null)}>Cancel</AdminButton>
            <AdminButton type="button" disabled={testBusy || !testRecipient} onClick={() => void sendTest()}>{testBusy ? 'Sending…' : 'Send test'}</AdminButton>
          </>
        )}
      >
        <AdminField label="Recipient" hint="Only approved owner notification inboxes are available.">
          <AdminSelect value={testRecipient} onChange={(event) => setTestRecipient(event.target.value)} disabled={testBusy}>
            {overview.allowedTestRecipients.map((recipient) => <option key={recipient} value={recipient}>{recipient}</option>)}
          </AdminSelect>
        </AdminField>
        <p className="email-notifications__test-warning">The subject is prefixed with [TEST]. No order record or production customer email event is created.</p>
      </AdminModal>
    </AdminShell>
  )
}
